interface LockBodyScrollOptions {
  root?: Document;
  allowTouchMoveWithin?: HTMLElement | null;
}

interface ScrollLockState {
  count: number;
  scrollY: number;
  previous: {
    htmlOverflow: string;
    bodyPosition: string;
    bodyTop: string;
    bodyLeft: string;
    bodyRight: string;
    bodyWidth: string;
    bodyOverflow: string;
    bodyTouchAction: string;
    bodyOverscrollBehavior: string;
  };
  allowedTouchMoveWithin: Map<symbol, HTMLElement | null>;
  onTouchMove: (event: TouchEvent) => void;
}

const activeScrollLocks = new WeakMap<Document, ScrollLockState>();

function syncBodyTouchAction(root: Document, state: ScrollLockState) {
  const hasAllowedTouchRegion = [...state.allowedTouchMoveWithin.values()].some(Boolean);
  root.body.style.touchAction = hasAllowedTouchRegion ? state.previous.bodyTouchAction : 'none';
}

export function lockBodyScroll(options: LockBodyScrollOptions = {}) {
  const root = options.root ?? document;
  const { body, documentElement } = root;
  const lockId = Symbol('body-scroll-lock');
  let state = activeScrollLocks.get(root);

  if (!state) {
    const scrollY = window.scrollY;
    const previous = {
      htmlOverflow: documentElement.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyOverflow: body.style.overflow,
      bodyTouchAction: body.style.touchAction,
      bodyOverscrollBehavior: body.style.overscrollBehavior,
    };

    documentElement.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';

    state = {
      count: 0,
      scrollY,
      previous,
      allowedTouchMoveWithin: new Map(),
      onTouchMove: (event: TouchEvent) => {
        const target = event.target as Node | null;
        const currentState = activeScrollLocks.get(root);
        if (!currentState) return;
        for (const allowedTouchMoveWithin of currentState.allowedTouchMoveWithin.values()) {
          if (allowedTouchMoveWithin && target && allowedTouchMoveWithin.contains(target)) return;
        }
        event.preventDefault();
      },
    };

    root.addEventListener('touchmove', state.onTouchMove, { passive: false });
    activeScrollLocks.set(root, state);
  }

  state.count += 1;
  state.allowedTouchMoveWithin.set(lockId, options.allowTouchMoveWithin ?? null);
  syncBodyTouchAction(root, state);

  return () => {
    const currentState = activeScrollLocks.get(root);
    if (!currentState) return;

    currentState.allowedTouchMoveWithin.delete(lockId);
    currentState.count = Math.max(0, currentState.count - 1);

    if (currentState.count > 0) {
      syncBodyTouchAction(root, currentState);
      return;
    }

    root.removeEventListener('touchmove', currentState.onTouchMove);
    documentElement.style.overflow = currentState.previous.htmlOverflow;
    body.style.position = currentState.previous.bodyPosition;
    body.style.top = currentState.previous.bodyTop;
    body.style.left = currentState.previous.bodyLeft;
    body.style.right = currentState.previous.bodyRight;
    body.style.width = currentState.previous.bodyWidth;
    body.style.overflow = currentState.previous.bodyOverflow;
    body.style.touchAction = currentState.previous.bodyTouchAction;
    body.style.overscrollBehavior = currentState.previous.bodyOverscrollBehavior;
    window.scrollTo(0, currentState.scrollY);
    activeScrollLocks.delete(root);
  };
}
